import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from "@mui/material";
import { useRouter } from "next/router";
import { useState } from "react";
import { ORDER_TYPE_ENUM, type OrderType } from "@/constants/enums";
import { MODAL_VIEW } from "@/constants/views";
import { useBranch } from "@/context/BranchContext";
import { orderTypeRoute, rememberOrderType } from "@/lib/orderingUi";
import { useAppDispatch, useAppSelector } from "@/store";
import { openModal } from "@/store/slices/commonSlice";
import { setResumedOrderId, setTable } from "@/store/slices/cartSlice";

export default function OrderTypeMenu() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(s => s.settings.data);
  const isStaff = useAppSelector(s => s.auth.isStaff);
  const orderType = useAppSelector(s => s.cart.orderType);
  const { branches, openPicker } = useBranch();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const options = [
    { type: ORDER_TYPE_ENUM.TAKE_AWAY, label: settings.takeaway_is_pickup ? "Pickup" : "Takeaway", enabled: settings.web_enable_takeaway, icon: <ShoppingBagOutlinedIcon fontSize="small" /> },
    { type: ORDER_TYPE_ENUM.DELIVERY, label: "Delivery", enabled: settings.web_enable_delivery, icon: <DeliveryDiningIcon fontSize="small" /> },
    { type: ORDER_TYPE_ENUM.DINE_IN, label: "Dine-in", enabled: isStaff && settings.web_enable_dine_in, icon: <TableRestaurantIcon fontSize="small" /> },
  ];
  const current = options.find(option => option.type === orderType);
  const choose = async (type: OrderType) => {
    setAnchor(null);
    if (type === orderType && !router.pathname.startsWith("/orders/")) return;
    setBusy(true);
    setError("");
    try {
      const changed = await router.push(orderTypeRoute(type, isStaff));
      if (changed) {
        rememberOrderType(settings._id, isStaff, type);
        if (type !== ORDER_TYPE_ENUM.DINE_IN) {
          dispatch(setTable(null));
          dispatch(setResumedOrderId(null));
        }
      }
    } catch { setError("Could not change order type. Please try again."); }
    finally { setBusy(false); }
  };
  return <Stack minWidth={0}>
    {error && <span role="alert">{error}</span>}
    <Button variant="outlined" size="small" startIcon={current?.icon} endIcon={<ExpandMoreIcon />}
      aria-label={`Order type: ${current?.label ?? "Choose"}`} aria-haspopup="menu" aria-expanded={Boolean(anchor)}
      disabled={busy} onClick={event => setAnchor(event.currentTarget)}
      sx={{ borderRadius: 1.5, minHeight: 34, px: { xxs: 0.5, sm: 1.5 }, fontSize: { xxs: 11, sm: 13 }, fontWeight: 700, textTransform: "none", whiteSpace: "nowrap", "& .MuiButton-startIcon": { mr: 0.5 }, "& .MuiButton-endIcon": { ml: 0.25 } }}>
      {current?.label ?? "Order type"}
    </Button>
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
      PaperProps={{ sx: { minWidth: 235, borderRadius: 2, mt: 0.5, p: 0.5 } }}>
      {options.filter(option => option.enabled).map(option => <MenuItem key={option.type} selected={orderType === option.type}
        sx={{ minHeight: 48, borderRadius: 1 }} onClick={() => void choose(option.type)}>
        <ListItemIcon>{option.icon}</ListItemIcon><ListItemText>{option.label}</ListItemText>
        {orderType === option.type && <CheckIcon fontSize="small" color="primary" />}
      </MenuItem>)}
      {orderType === ORDER_TYPE_ENUM.DELIVERY && <MenuItem onClick={() => { setAnchor(null); dispatch(openModal(MODAL_VIEW.DELIVERY_INFO)); }}>Delivery address</MenuItem>}
      {branches.length > 1 && <MenuItem onClick={() => { setAnchor(null); openPicker(); }}>Change branch</MenuItem>}
      {isStaff && settings.web_enable_dine_in && [<Divider key="divider" />, <MenuItem key="floor" onClick={() => { setAnchor(null); void router.push("/staff/tables"); }}>Floor Plan</MenuItem>]}
    </Menu>
  </Stack>;
}
